<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import AssistantBusinessBrainForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantBusinessBrainForm.vue';
import AssistantMarketingForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantMarketingForm.vue';
import AssistantStarterPacks from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantStarterPacks.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const assistantId = computed(() => Number(route.params.assistantId));
const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingItem);
const assistant = computed(() =>
  store.getters['captainAssistants/getRecord'](assistantId.value)
);
const activeTab = ref('shop');
const tabs = computed(() => [
  { label: t('CAPTAIN.BRAIN.TABS.SHOP'), value: 'shop' },
  { label: t('CAPTAIN.BRAIN.TABS.MARKETING'), value: 'marketing' },
]);
const activeTabIndex = computed(() =>
  activeTab.value === 'marketing' ? 1 : 0
);

const handleTabChange = tab => {
  activeTab.value = tab?.value === 'marketing' ? 'marketing' : 'shop';
};

watch(
  assistantId,
  newId => {
    if (newId) store.dispatch('captainAssistants/show', newId);
  },
  { immediate: true }
);

const handleSubmit = async updatedAssistant => {
  try {
    await store.dispatch('captainAssistants/update', {
      id: assistantId.value,
      ...updatedAssistant,
    });
    useAlert(t('CAPTAIN.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('CAPTAIN.ASSISTANTS.EDIT.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <PageLayout
    :header-title="t('CAPTAIN.BRAIN.HEADER')"
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template #body>
      <div class="flex flex-col gap-6 pb-8">
        <SettingsHeader
          :heading="t('CAPTAIN.BRAIN.TITLE')"
          :description="t('CAPTAIN.BRAIN.DESCRIPTION')"
        />
        <TabBar
          :tabs="tabs"
          :initial-active-tab="activeTabIndex"
          @tab-changed="handleTabChange"
        />
        <template v-if="activeTab === 'shop'">
          <AssistantStarterPacks v-if="assistant" :assistant="assistant" />
          <AssistantBusinessBrainForm
            v-if="assistant"
            :key="assistant.config?.training?.applied_pack || 'none'"
            :assistant="assistant"
            @submit="handleSubmit"
          />
        </template>
        <AssistantMarketingForm
          v-else-if="assistant"
          :assistant="assistant"
          @submit="handleSubmit"
        />
      </div>
    </template>
  </PageLayout>
</template>
